import {
  CurrencyDetails,
  PriceFormatSettingWithTemplate,
  ProviderSignupSource,
  ProviderLifecycleStatus,
  MenuEditorSettings,
  DineInSettings,
  DineOutSettings,
  DeductionsReportSettings,
} from '../../api/openapi_gen/deliveryProviderPortal/deliveryProvider/apis';

import { LocaleType } from '../../utils/translations_types';

import { PromotionAccessType } from '../../api/openapi_gen/deliveryProviderPortal/deliveryCampaignProvider/apis';

export { AccountData } from '../../api/openapi_gen/deliveryProviderPortal/deliveryProvider/apis';

export interface VendorAssignedProvider {
  vendor_id: number;
  vendor_name: string;
  providers: ProviderPortalAssignedProvider[];
}

export interface ProviderPortalAssignedProvider {
  provider_id: number;
  provider_name: string;

  vendor_id: number;
  vendor_name: string;

  city_id: number;
  country_code: string;
  currency: CurrencyDetails;
  price_format_setting: PriceFormatSettingWithTemplate | null;
  city_locales: LocaleType[];
  city_main_locale: LocaleType;
  campaigns_settings?: CampaignsSettings;
  signup_source: ProviderSignupSource;
  lifecycle_status: ProviderLifecycleStatus;
  timezone: string;

  menu_editor_settings?: MenuEditorSettings;
  has_city_subject_to_bulgaria_euro_change: boolean;
  bulgaria_currency_change_timestamp?: number;
  dine_in_settings: DineInSettings;
  dine_out_settings: DineOutSettings;
  deductions_report_settings: DeductionsReportSettings;
}

export interface ProviderPortalSelectedProvider {
  providerId: number;
  providerName: string;

  vendorId: number;
  vendorName: string;

  cityId: number;
  countryCode: string;
  currency: CurrencyDetails;
  timezone: string;
  city_locales: LocaleType[];
  city_main_locale: LocaleType;
  signup_source: ProviderSignupSource;
  lifecycle_status: ProviderLifecycleStatus;

  menuEditorSettings?: MenuEditorSettings;
  campaigns_settings?: CampaignsSettings;

  permissions: AccountPermissions;

  isSubjectToBulgariaEuroChange?: boolean;
  bulgariaEuroChangeTimestamp?: number;
}

export interface SelectedVendorAndProvider {
  providerId: number;
  providerName: string;

  vendorId: number;
  vendorName: string;
}

export interface CampaignsSettings {
  can_self_assign_campaign: boolean;
  can_manage_promotions?: boolean;
  is_menu_inflation?: boolean;
  is_metrics_satisfied?: boolean;
  can_setup_bolt_plus_promotions?: boolean;
  can_manage_smart_promotions?: boolean;
  promotion_access_type?: PromotionAccessType;
}

export interface AccountPermissions {
  menuEditorAccess: boolean;
  selfAssignCampaigns: boolean;
  canSeePromotions: boolean;
  canSeeSmartPromotions: boolean;
  canCreateFoodPromotions: boolean;
  canCreatePromotions: boolean;
  canSetupBoltPlusPromotions: boolean;
  dineInAccess: boolean;
  dineOutAccess: boolean;
  deductionsAccess: boolean;
  canCreateSmartPromotionOnly: boolean;
  canCreateItemsPromotionOnly: boolean;
  canCreateAllPromotion: boolean;
}

export enum AccountStage {
  ONBOARDING = 'onboarding',
  ACTIVATED = 'activated',
}

export function mapAssignedProviderToSelectedProvider(
  provider: ProviderPortalAssignedProvider,
): ProviderPortalSelectedProvider {
  return {
    providerId: provider.provider_id,
    providerName: provider.provider_name,

    vendorId: provider.vendor_id,
    vendorName: provider.vendor_name,

    cityId: provider.city_id,
    countryCode: provider.country_code,
    timezone: provider.timezone,
    currency: provider.currency,

    city_locales:
      provider.city_locales && provider.city_locales.length
        ? provider.city_locales
        : [LocaleType.English],
    city_main_locale: provider.city_main_locale ?? LocaleType.English,

    menuEditorSettings: provider.menu_editor_settings,

    isSubjectToBulgariaEuroChange: provider.has_city_subject_to_bulgaria_euro_change,
    bulgariaEuroChangeTimestamp: provider.bulgaria_currency_change_timestamp,

    campaigns_settings: provider.campaigns_settings,
    signup_source: provider.signup_source,
    lifecycle_status: provider.lifecycle_status,

    permissions: {
      menuEditorAccess: Boolean(provider.menu_editor_settings?.is_enabled),
      selfAssignCampaigns: Boolean(provider.campaigns_settings?.can_self_assign_campaign),
      canSeePromotions: Boolean(
        provider.campaigns_settings?.can_self_assign_campaign ||
          provider.campaigns_settings?.can_manage_promotions,
      ),
      canSeeSmartPromotions: Boolean(provider.campaigns_settings?.can_manage_smart_promotions),
      canCreateFoodPromotions: !provider.campaigns_settings?.is_menu_inflation,
      canCreatePromotions: Boolean(provider.campaigns_settings?.is_metrics_satisfied),
      canSetupBoltPlusPromotions: Boolean(
        provider.campaigns_settings?.can_setup_bolt_plus_promotions,
      ),
      dineInAccess: Boolean(
        provider.dine_in_settings.is_order_and_pay_enabled ||
          provider.dine_in_settings.is_pay_and_go_enabled,
      ),
      dineOutAccess: Boolean(provider.dine_out_settings?.is_enabled),
      deductionsAccess: provider.deductions_report_settings.is_deductions_report_enabled,
      canCreateSmartPromotionOnly: Boolean(
        provider.campaigns_settings?.promotion_access_type === PromotionAccessType.SMART,
      ),
      canCreateItemsPromotionOnly: Boolean(
        provider.campaigns_settings?.promotion_access_type === PromotionAccessType.SELECTED_ITEMS,
      ),
      canCreateAllPromotion: Boolean(
        provider.campaigns_settings?.promotion_access_type === PromotionAccessType.ALL,
      ),
    },
  };
}

export function mapSelectedProviderDataToSelectedVendorAndProvider(
  provider: ProviderPortalSelectedProvider,
): SelectedVendorAndProvider {
  return {
    providerId: provider.providerId,
    providerName: provider.providerName,
    vendorId: provider.vendorId,
    vendorName: provider.vendorName,
  };
}

export function getSelectedProviderData(
  selectedProvider: number,
  assignedProviders: ProviderPortalAssignedProvider[],
): ProviderPortalSelectedProvider | null {
  if (!assignedProviders?.length) {
    return null;
  }

  const firstProvider = assignedProviders[0];

  // if we do not have selected provider, we return first 1 as selected
  if (!selectedProvider) {
    return mapAssignedProviderToSelectedProvider(firstProvider);
  }

  // we search for selected provider
  const providerData = assignedProviders?.find((item) => item.provider_id === selectedProvider);

  // if we do not have selected provider, we return first 1 as selected
  return mapAssignedProviderToSelectedProvider(providerData || firstProvider);
}

export type SelectedProvider = ReturnType<typeof mapAssignedProviderToSelectedProvider>;
export type ProviderPermissions = ReturnType<
  typeof mapAssignedProviderToSelectedProvider
>['permissions'];
